import { bopApi } from '@/lib/api/client';

const FX_SWAPS_PATH = 'v1/fx/swaps';

export type FxRecordStatus = 'SUCCESS' | 'FAILED';

/** Monex quote payload attached to successful swaps (verbatim wire shape). */
export interface QuoteResponse {
  id: string;
  rate: number;
  expirationTime: string;
  sellCurrencyCode: string;
  buyCurrencyCode: string;
  sellAmount: number;
  buyAmount: number;
  amountFixed: 'sell' | 'buy';
  valueDate: string;
  tradeType: 'sameDay' | 'tom' | 'spot' | 'forward';
  clientReference?: string;
  settlementAccountId?: string;
}

/** Executed trade payload attached to successful swaps (verbatim wire shape). */
export interface TradeResponse {
  id: string;
  code: string;
  contractDate: string;
  valueDate: string;
  rate: number;
  sellCurrencyCode: string;
  sellAmount: number;
  buyCurrencyCode: string;
  buyAmount: number;
  amountFixed: 'sell' | 'buy';
  status: 'awaitingSettlement' | 'readyForDelivery' | 'delivered' | 'reversed';
  tradeType: 'sameDay' | 'tom' | 'spot' | 'forward';
  createdAt: string;
  clientReference?: string;
}

/** Raw Dynamo record from GET /v1/fx/swaps — the single-letter keys are real. */
export interface FxRecord {
  /** DynamoDB partition key (internal, but deliberately displayed in the detail dialog) */
  p: string;
  /** sort key: '<timestamp>_<status>' */
  s: string;
  /** GSI attribute */
  status: FxRecordStatus;
  /** ISO timestamp (GSI) */
  created_at: string;
  client_reference: string;
  /** user id from the payload */
  fast_id: string;
  /** SQS message id */
  message_id?: string;
  /** e.g. 'MONEX' */
  provider: string;
  from_currency: string;
  to_currency: string;
  amount: number;
  /** rate charged to the customer */
  customer_price?: number;
  /** rate we executed at */
  trade_price?: number;
  /** customer_price − trade rate */
  pnl?: number;
  /** success case */
  quote?: QuoteResponse;
  /** success case */
  trade?: TradeResponse;
  /** failure case */
  error_code?: string;
  /** failure case */
  error_message?: string;
}

// The legacy Nuxt BFF re-wrapped this as { success, data } — the envelope died with the
// BFF: the SPA consumes { records, cursor } raw and lets ky/TanStack throw.
export interface FxSwapsWireResponse {
  records: FxRecord[];
  /** present ⇔ more pages exist; opaque, produced by the trade-engine lambda */
  cursor?: string;
}

/** API-routed half of the hybrid filter split. */
export interface FxSwapsApiFilters {
  /** ISO 8601 — local start-of-day of the picked From date */
  startTime?: string;
  /** ISO 8601 — local end-of-day of the picked To date */
  endTime?: string;
  status?: FxRecordStatus;
}

export interface FilterOption {
  label: string;
  value: string;
}

/** Status filter options (exact labels/values). */
export const FX_SWAP_STATUS_OPTIONS: ReadonlyArray<{ label: string; value: FxRecordStatus }> = [
  { label: 'Success', value: 'SUCCESS' },
  { label: 'Failed', value: 'FAILED' },
];

/** Client-side filter values: multi-choice list, text substring, numeric range. */
export type FxSwapClientFilterValue = string | ReadonlyArray<string | FilterOption> | { from?: number | string; to?: number | string };

/** `created_at` date filter — `{ from?, to? }` local start/end-of-day Dates. */
export interface FxSwapDateRange {
  from?: Date | string;
  to?: Date | string;
}

export type FxSwapFilterValue = FxSwapClientFilterValue | FxSwapDateRange;

/** Flat per-column filter map, exactly as the legacy table's filter callback delivered it. */
export type FxSwapFilters = Record<string, FxSwapFilterValue>;

export interface FxSwapFilterSplit {
  apiFilters: FxSwapsApiFilters;
  clientFilters: Record<string, FxSwapClientFilterValue>;
}

const toIsoString = (value: Date | string): string => (typeof value === 'string' ? value : new Date(value).toISOString());

/**
 * Hybrid filter split (exact rules): `created_at` → startTime/endTime,
 * `status` → status, every other key stays client-side. Client-side filters are
 * dormant today — no current column routes there — but the machinery is
 * ported so future filterable columns only need a config entry.
 */
export const splitFxSwapFilters = (filters: FxSwapFilters): FxSwapFilterSplit => {
  const apiFilters: FxSwapsApiFilters = {};
  const clientFilters: Record<string, FxSwapClientFilterValue> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (key === 'created_at') {
      const range = value as FxSwapDateRange;
      if (range.from !== undefined) apiFilters.startTime = toIsoString(range.from);
      if (range.to !== undefined) apiFilters.endTime = toIsoString(range.to);
    } else if (key === 'status') {
      // A multi-value select takes the FIRST element; forwarded only when it is exactly
      // SUCCESS/FAILED (legacy guard)
      const first: unknown = Array.isArray(value) ? value[0] : value;
      if (first === 'SUCCESS' || first === 'FAILED') apiFilters.status = first;
    } else {
      clientFilters[key] = value as FxSwapClientFilterValue;
    }
  }
  return { apiFilters, clientFilters };
};

const unwrapOptionValue = (option: string | FilterOption): string => (typeof option === 'string' ? option : option.value);

// NaN bounds are ignored
const toRangeBound = (bound: number | string | undefined): number | undefined => {
  if (bound === undefined) return undefined;
  const parsed = typeof bound === 'string' ? Number.parseFloat(bound) : bound;
  return Number.isNaN(parsed) ? undefined : parsed;
};

// Array.isArray can't narrow readonly arrays out of a union — explicit predicate needed
const isRangeFilter = (filter: FxSwapClientFilterValue): filter is Extract<FxSwapClientFilterValue, { from?: unknown }> =>
  typeof filter === 'object' && filter !== null && !Array.isArray(filter);

const passesClientFilter = (recordValue: unknown, filter: FxSwapClientFilterValue): boolean => {
  if (Array.isArray(filter)) {
    // Array filter: exclude only when the record value is non-nullish AND not in the
    // list — nullish record values PASS array filters (intended legacy behaviour)
    const allowed = (filter as ReadonlyArray<string | FilterOption>).map(unwrapOptionValue);
    return recordValue === null || recordValue === undefined || allowed.some((value) => value === recordValue);
  }
  if (typeof filter === 'string') {
    // String filter: case-insensitive substring; only excludes when the record value is
    // a string that doesn't contain it — non-string values pass
    return typeof recordValue !== 'string' || recordValue.toLowerCase().includes(filter.toLowerCase());
  }
  // Range filter: applies only when the record value is a number; inclusive bounds
  if (typeof recordValue !== 'number' || !isRangeFilter(filter)) return true;
  const from = toRangeBound(filter.from);
  const to = toRangeBound(filter.to);
  if (from !== undefined && recordValue < from) return false;
  if (to !== undefined && recordValue > to) return false;
  return true;
};

// NOTE: an unknown/typo'd key returns undefined, which passes every filter branch
// (silent no-op). Constrain to keyof FxRecord at the column config when the dormant
// client-filter machinery gains its first real column.
const getRecordValue = (record: FxRecord, key: string): unknown => record[key as keyof FxRecord];

/** Per-record predicate: ALL active client-side filters must pass. */
export const matchesFxSwapClientFilters = (record: FxRecord, clientFilters: Record<string, FxSwapClientFilterValue>): boolean =>
  Object.entries(clientFilters).every(([key, filter]) => passesClientFilter(getRecordValue(record, key), filter));

/** Applies the client-side half of the split to the accumulated rows — no refetch. */
export const applyFxSwapClientFilters = (records: FxRecord[], clientFilters: Record<string, FxSwapClientFilterValue>): FxRecord[] =>
  Object.keys(clientFilters).length === 0 ? records : records.filter((record) => matchesFxSwapClientFilters(record, clientFilters));

// Local start/end-of-day: the picked calendar date becomes local midnight /
// 23:59:59.999, so the ISO strings sent upstream embed the operator's TZ offset —
// deliberate legacy behaviour, kept. Now shared from utils/date; re-exported because
// fx-swaps-panel.tsx imports the pair from this module.
export { toLocalEndOfDay, toLocalStartOfDay } from '@/utils/date';

/**
 * Query-param builder for GET /v1/fx/swaps. `limit` is deliberately never sent (legacy
 * parity) — page size and cursor encoding are owned by the trade-engine
 * lambda; the cursor round-trips verbatim as an opaque token.
 */
export const buildFxSwapsSearchParams = (filters: FxSwapsApiFilters, cursor?: string): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.startTime) params.set('startTime', filters.startTime);
  if (filters.endTime) params.set('endTime', filters.endTime);
  if (filters.status) params.set('status', filters.status);
  if (cursor) params.set('cursor', cursor);
  return params;
};

export const getFxSwapsPage = (filters: FxSwapsApiFilters, cursor?: string) =>
  bopApi.get(FX_SWAPS_PATH, { searchParams: buildFxSwapsSearchParams(filters, cursor) }).json<FxSwapsWireResponse>();
